import { useResource } from "hooks/useResource";
import { getAbout, getManagement } from "services/content";
import PageHero from "components/shared/PageHero";
import SectionLabel from "components/shared/SectionLabel";
import MgmtCard from "components/shared/MgmtCard";
import Reveal from "components/shared/Reveal";
import Skeleton from "components/shared/Skeleton";

export default function AboutPage() {
  const { data: about } = useResource(getAbout);
  const { data: management } = useResource(getManagement);

  const story = about?.story || [];
  const pillars = about?.pillars || [];
  const timeline = about?.timeline || [];

  return (
    <div className="animate-fadeIn">
      <PageHero
        title="About Our Institution"
        sub="Three decades of nurturing Khulna's brightest young women with quality education and strong values."
        page="About"
      />
      <section className="max-w-container mx-auto px-4 sm:px-10 py-[68px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-[60px] items-center mb-20">
          <div>
            <SectionLabel label="Our Story" />
            <h2 className="font-serif text-[34px] font-bold text-green-dark mb-5 leading-[1.25]">
              A Legacy of Women's Education in Khulna
            </h2>
            {about ? (
              story.map((para, i) => (
                <p
                  key={i}
                  className="text-muted leading-[1.85] mb-4 text-[15px]"
                >
                  {para}
                </p>
              ))
            ) : (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {!about
              ? Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-[150px] rounded-[14px]" />
                ))
              : pillars.map(({ label, icon, text }, i) => (
                  <Reveal
                    key={label}
                    delay={i * 90}
                    className="bg-white border border-line rounded-[14px] px-[18px] py-[22px] shadow-[0_2px_12px_rgba(0,0,0,0.05)] transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="text-[28px] mb-2.5">{icon}</div>
                    <div className="font-serif font-bold text-base text-green-dark mb-2">
                      {label}
                    </div>
                    <p className="text-[13px] text-muted leading-[1.6]">
                      {text}
                    </p>
                  </Reveal>
                ))}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <SectionLabel label="Our History" center />
          <h2 className="font-serif text-[34px] font-bold text-green-dark text-center mb-12">
            Key Milestones
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-line -translate-x-1/2" />
            {timeline.map(({ year, text }, i) => (
              <Reveal
                key={year}
                className="flex mb-8 relative"
                style={{ justifyContent: i % 2 === 0 ? "flex-start" : "flex-end" }}
              >
                <div
                  className="w-[44%]"
                  style={{ padding: i % 2 === 0 ? "0 32px 0 0" : "0 0 0 32px" }}
                >
                  <div className="bg-white border border-line rounded-xl px-5 py-[18px] shadow-[0_2px_12px_rgba(0,0,0,0.05)]">
                    <div className="font-serif text-[22px] font-bold text-green mb-1.5">
                      {year}
                    </div>
                    <p className="text-sm text-muted leading-[1.6]">{text}</p>
                  </div>
                </div>
                <div className="absolute left-1/2 top-[18px] w-3.5 h-3.5 rounded-full bg-green border-[3px] border-white -translate-x-1/2 shadow-[0_0_0_3px_#e6f2ed]" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Management full */}
      <section className="bg-green-dark px-4 sm:px-10 py-[68px]">
        <div className="max-w-container mx-auto">
          <div className="text-center mb-11">
            <SectionLabel color="#8ec4b0" label="Leadership" center />
            <h2 className="font-serif text-[28px] sm:text-[38px] font-bold text-white">
              Governing Body &amp; Management
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[18px]">
            {!management
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} dark className="h-[210px] rounded-[14px]" />
                ))
              : management.map((p, i) => (
                  <Reveal key={p.name} delay={(i % 3) * 90}>
                    <MgmtCard person={p} />
                  </Reveal>
                ))}
          </div>
        </div>
      </section>
    </div>
  );
}
